import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';
import { teamAPI } from '../services/api';
import { useSystemStats } from '../hooks/useSystemStats';
import { phase1Team, phase3Team } from '../data/teams';
import './Homepage.css';

export function About() {
  // Real-time statistics from shared hook
  const { farmerCount, processorCount, municipalityCount, totalHectares, loading: statsLoading } = useSystemStats();

  // Team members state (Phase 2 - from database)
  const [teamMembers, setTeamMembers] = useState({
    members: [],
    loading: true
  });

  // State to track selected phase and dropdown visibility
  const [selectedPhase, setSelectedPhase] = useState(3);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Flipping logos state
  const aboutLogos = [
    { src: '/Bisu-logo.png', alt: 'BISU Logo' },
    { src: '/bohol-cacao-logo.png', alt: 'Bohol Cacao Logo' },
    { src: '/DOST-logo.png', alt: 'DOST Logo' },
  ];
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLogoIndex(prev => (prev + 1) % aboutLogos.length);
    }, 1000);
    return () => clearInterval(interval);
  }, [aboutLogos.length]);

  // Fetch team members from database
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await teamAPI.getAll();
        // Backend returns { success: true, data: [...] }
        const members = response?.data?.data || response?.data || [];
        setTeamMembers({
          members,
          loading: false
        });
      } catch (error) {
        setTeamMembers(prev => ({ ...prev, loading: false, error: 'Unable to load team members.' }));
      }
    };

    fetchTeamMembers();
  }, []);

  const features = [
    {
      title: 'Comprehensive Farmer & Processor Directory',
      description: 'Detailed profiles with farm size, cacao varieties, production capacity, contact details, and GPS coordinates. Search by name, location, age, or size.'
    },
    {
      title: 'Interactive Bohol Map',
      description: 'Geospatial visualization of farmers and processors across all 47 Bohol municipalities with GPS coordinates and location markers.'
    },
    {
      title: 'Advanced Analytics Dashboard',
      description: 'Interactive charts for demographics, production trends, farm ownership analysis, cacao variety distribution, and processing capacity metrics.'
    },
    {
      title: 'Excel Import/Export System',
      description: 'Bulk import data from Excel with automatic field mapping and validation. Export filtered data for offline analysis and reporting.'
    },
    {
      title: 'Multi-Level Search & Filtering',
      description: 'Filter by municipality, gender, ownership, capacity, and type. Search across multiple fields for optimal results.'
    },
    {
      title: 'User Authentication & Role Management',
      description: 'Role-based access control with admin and regular user permissions. Admins can manage records while users have view-only access.'
    },
    {
      title: 'Data Privacy & Security',
      description: 'Contact information is masked for non-admin users to protect privacy. Full details visible only to administrators.'
    },
    {
      title: 'Real-time Statistics',
      description: 'Live metrics showing total farmers, processors, municipalities covered, and farm area with automatic updates.'
    },
    {
      title: 'Responsive Design',
      description: 'Mobile-friendly interface built with modern React and Tailwind CSS for all screen sizes.'
    }
  ];

  const stakeholders = [
    {
      title: 'Cacao Farmers',
      description: 'Individual farmers and cooperatives growing cacao across Bohol\'s 47 municipalities.',
      benefits: ['Register farm data for visibility', 'Track production records', 'Access industry insights', 'Benefit from data-driven programs', 'Participate in agricultural surveys']
    },
    {
      title: 'Cacao Processors',
      description: 'Processing facilities, chocolate makers, and manufacturers transforming cacao into finished products.',
      benefits: ['Register processing facility data', 'View industry statistics', 'Analyze regional production capacity', 'Access comprehensive industry data', 'Track processing capacity trends']
    },
    {
      title: 'Government Agencies',
      description: 'Department of Agriculture, LGUs, and government organizations supporting the cacao industry.',
      benefits: ['Data-driven policy making', 'Agricultural program planning', 'Resource allocation optimization', 'Impact assessment tracking', 'Industry development monitoring']
    },
    {
      title: 'Researchers & Academics',
      description: 'Universities, research institutions, and students studying the cacao industry.',
      benefits: ['Comprehensive industry data access', 'Statistical analysis capabilities', 'Research collaboration opportunities', 'Trend analysis and forecasting', 'Publication-ready datasets']
    },
    {
      title: 'NGOs & Development Partners',
      description: 'Non-profit organizations and international partners supporting sustainable cacao farming.',
      benefits: ['Identify intervention areas', 'Monitor program effectiveness', 'Stakeholder mapping', 'Capacity building planning', 'Impact measurement']
    },
    {
      title: 'Investors & Business Sector',
      description: 'Private sector investors and businesses interested in the cacao value chain.',
      benefits: ['Market opportunity analysis', 'Supply chain assessment', 'Investment decision support', 'Partnership identification', 'Risk evaluation']
    }
  ];

  // Dynamic system statistics based on real-time data
  const displayStats = [
    { 
      label: 'Registered Farmers', 
      value: statsLoading ? 'Loading...' : farmerCount.toLocaleString()
    },
    { 
      label: 'Active Processors', 
      value: statsLoading ? 'Loading...' : processorCount.toLocaleString()
    },
    { 
      label: 'Municipalities Covered', 
      value: statsLoading ? 'Loading...' : `${municipalityCount} out of 47`
    },
    { 
      label: 'Total Farm Area', 
      value: statsLoading ? 'Loading...' : `${totalHectares.toLocaleString()} hectares`
    }
  ];

  return (
    <div className="min-h-screen py-8 bg-gradient-to-br from-gray-50 via-amber-50/20 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up" style={{ opacity: 0 }}>
          <h1 className="mb-6 text-5xl font-bold bg-gradient-to-r from-amber-900 via-orange-700 to-green-800 bg-clip-text text-transparent">Bohol Cacao Industry Information System (BCIIS)</h1>
          <p className="text-gray-600 max-w-3xl mx-auto mb-8 text-lg leading-relaxed">
            A digital platform for managing and analyzing Bohol's cacao industry through real-time data analytics, 
            geospatial mapping, and industry information. BCIIS serves farmers, processors, researchers, 
            government agencies, and development partners with actionable insights.
          </p>
          <div className="flex justify-center items-center gap-6 animate-scale-in" style={{ opacity: 0, animationDelay: '0.2s' }}>
            {aboutLogos.map((logo, index) => (
              <div
                key={logo.alt}
                className="relative"
                style={{
                  width: 100,
                  height: 100,
                  perspective: '400px',
                }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className={`rounded-full border-4 border-amber-300 object-cover shadow-lg ${
                    currentLogoIndex === index ? 'hero-logo-active' : 'hero-logo-inactive'
                  }`}
                  style={{
                    width: 100,
                    height: 100,
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="border-0 shadow-xl card-hover animate-slide-in-left" style={{ opacity: 0, animationDelay: '0.3s' }}>
            <CardHeader>
              <CardTitle className="text-amber-900">
                Our Mission
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                To create a data-driven platform that manages and analyzes the cacao value chain in Bohol. 
                We provide real-time analytics and visualization tools for sustainable growth and informed decision-making.
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-xl card-hover animate-slide-in-right" style={{ opacity: 0, animationDelay: '0.4s' }}>
            <CardHeader>
              <CardTitle className="text-green-800">
                Our Vision
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                To establish Bohol as a model for sustainable, technology-driven cacao production in the Philippines. 
                We envision a thriving ecosystem supported by data intelligence where stakeholders prosper through 
                informed decisions and evidence-based policies.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* System Statistics */}
        <div className="bg-gradient-to-r from-amber-900 to-green-800 text-white p-8 rounded-lg mb-12">
          <h2 className="text-center mb-8 text-white">Real-Time System Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {displayStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl mb-2 text-amber-200">{stat.value}</div>
                <div className="text-sm text-amber-100">{stat.label}</div>
              </div>
            ))}
          </div>
          {statsLoading && (
            <div className="text-center mt-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-amber-200"></div>
              <p className="text-amber-100 text-sm mt-2">Loading real-time data...</p>
            </div>
          )}
        </div>

        {/* Key Features */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-amber-900">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-green-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-green-800">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Stakeholders */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-amber-900">Who Benefits</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {stakeholders.map((stakeholder, index) => (
              <Card key={index} className="border-amber-200">
                <CardHeader>
                  <CardTitle className="text-green-800">{stakeholder.title}</CardTitle>
                  <CardDescription>{stakeholder.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stakeholder.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-center">
                        <span className="text-green-600 mr-2">✓</span>
                        <span className="text-sm text-gray-600">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Technology Stack */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-amber-900">Technology & Implementation</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800">Frontend Technology</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="secondary">React.js 18</Badge>
                  <Badge variant="secondary">Tailwind CSS</Badge>
                  <Badge variant="secondary">Lucide Icons</Badge>
                  <Badge variant="secondary">React Leaflet</Badge>
                  <Badge variant="secondary">Recharts</Badge>
                  <Badge variant="secondary">Axios</Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-amber-900">Core Features</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge variant="outline">Real-time Data</Badge>
                  <Badge variant="outline">GPS Mapping</Badge>
                  <Badge variant="outline">Excel Import/Export</Badge>
                  <Badge variant="outline">Role-Based Access</Badge>
                  <Badge variant="outline">Data Analytics</Badge>
                  <Badge variant="outline">Privacy Protection</Badge>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800">Data Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Badge>RESTful API</Badge>
                  <Badge>MySQL Database</Badge>
                  <Badge>Bulk Import</Badge>
                  <Badge>Data Validation</Badge>
                  <Badge>Secure Authentication</Badge>
                  <Badge>Session Management</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* System Capabilities */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-amber-900">System Capabilities</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800">For Farmers Directory</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Complete farmer profiles with 30+ data fields</li>
                  <li>• Search by name, age, farm size, barangay, or varieties</li>
                  <li>• Filter by municipality, gender, and land ownership</li>
                  <li>• View cacao varieties planted (UF18, BR25, K9, etc.)</li>
                  <li>• Track farm area, tree count, and productivity</li>
                  <li>• Organization and cooperative membership info</li>
                  <li>• Pagination (6 farmers per page)</li>
                  <li>• Bulk Excel import/export</li>
                  <li>• GPS coordinates for farm locations</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-amber-900">For Processors Directory</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Detailed processor and manufacturer profiles</li>
                  <li>• Search by name, address, or processing type</li>
                  <li>• Filter by municipality and capacity</li>
                  <li>• Processing capacity tracking (kg/day)</li>
                  <li>• Product types and certifications</li>
                  <li>• Contact information (protected for privacy)</li>
                  <li>• Pagination (6 processors per page)</li>
                  <li>• Comprehensive Excel import system</li>
                  <li>• Location mapping with GPS data</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-green-800">Analytics Dashboard</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Municipality-wise farmer distribution chart</li>
                  <li>• Processor distribution by location</li>
                  <li>• Gender demographics pie chart</li>
                  <li>• Age distribution histogram</li>
                  <li>• Land ownership analysis</li>
                  <li>• Cacao variety popularity chart</li>
                  <li>• Processing capacity analysis</li>
                  <li>• All charts are interactive and real-time</li>
                  <li>• Export charts and data for reports</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-amber-900">Interactive Map</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• All 47 Bohol municipalities mapped</li>
                  <li>• Click municipality to view farmers/processors</li>
                  <li>• GPS markers with precise coordinates</li>
                  <li>• Zoom and pan capabilities</li>
                  <li>• Real-time data updates</li>
                  <li>• Toggle between farmers and processors view</li>
                  <li>• Search specific locations</li>
                  <li>• Export map data for GIS analysis</li>
                  <li>• OpenStreetMap integration</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Development Team */}
        <div className="mb-12">
          <h2 className="text-center mb-8 text-amber-900">Meet the Team</h2>
          
          {/* Phase Selector Dropdown */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-600 to-amber-800 text-white rounded-lg shadow-lg hover:from-amber-700 hover:to-amber-900 transition-all duration-300"
                aria-expanded={isDropdownOpen}
                aria-haspopup="listbox"
                aria-label={`Select phase. Current: Phase ${selectedPhase}`}
              >
                <span className="font-semibold">Phase {selectedPhase}</span>
                <svg 
                  className={`w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-full mt-2 w-full bg-white rounded-lg shadow-xl border-2 border-amber-200 overflow-hidden z-10" role="listbox" aria-label="Select phase">
                  <button
                    role="option"
                    aria-selected={selectedPhase === 1}
                    onClick={() => {
                      setSelectedPhase(1);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full px-6 py-3 text-left hover:bg-amber-50 transition-colors ${selectedPhase === 1 ? 'bg-amber-100 font-semibold' : ''}`}
                  >
                    Phase 1
                  </button>
                  <button
                    role="option"
                    aria-selected={selectedPhase === 2}
                    onClick={() => {
                      setSelectedPhase(2);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full px-6 py-3 text-left hover:bg-amber-50 transition-colors ${selectedPhase === 2 ? 'bg-amber-100 font-semibold' : ''}`}
                  >
                    Phase 2
                  </button>
                  <button
                    role="option"
                    aria-selected={selectedPhase === 3}
                    onClick={() => {
                      setSelectedPhase(3);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full px-6 py-3 text-left hover:bg-amber-50 transition-colors ${selectedPhase === 3 ? 'bg-amber-100 font-semibold' : ''}`}
                  >
                    Phase 3
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Team Members Display */}
          <div className="max-w-6xl mx-auto">
            {/* Phase 1 Team */}
            {selectedPhase === 1 && (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 animate-fade-in-up">
                {phase1Team.map((member, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col items-center group cursor-pointer"
                  >
                    <div className="w-20 h-20 mb-2 rounded-full border-2 border-amber-300 shadow-md overflow-hidden transition-transform duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <ImageWithFallback
                        src={member.Photo_URL}
                        alt={member.Full_Name}
                        loading='lazy'
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 text-center leading-tight">{member.Full_Name}</h3>
                    <p className="text-xs text-amber-700 text-center">{member.Role}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Phase 2 Team (Database) */}
            {selectedPhase === 2 && (
              <div>
                {teamMembers.loading ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
                    <p className="text-gray-600 mt-4">Loading team members...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 animate-fade-in-up">
                    {teamMembers.members.map((member) => (
                      <div 
                        key={member.Team_Member_ID} 
                        className="flex flex-col items-center group cursor-pointer"
                      >
                        <div className="w-20 h-20 mb-2 rounded-full border-2 border-amber-300 shadow-md overflow-hidden transition-transform duration-300 group-hover:scale-110 group-hover:shadow-lg">
                          <ImageWithFallback
                            src={member.Photo_URL}
                            alt={member.Full_Name}
                            loading='lazy'
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-800 text-center leading-tight">{member.Full_Name}</h3>
                        <p className="text-xs text-amber-700 text-center">{member.Role}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Phase 3 Team */}
            {selectedPhase === 3 && (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 animate-fade-in-up">
                {phase3Team.map((member, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col items-center group cursor-pointer"
                  >
                    <div className="w-20 h-20 mb-2 rounded-full border-2 border-amber-300 shadow-md overflow-hidden transition-transform duration-300 group-hover:scale-110 group-hover:shadow-lg">
                      <ImageWithFallback
                        src={member.Photo_URL}
                        alt={member.Full_Name}
                        loading='lazy'
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 text-center leading-tight">{member.Full_Name}</h3>
                    <p className="text-xs text-amber-700 text-center">{member.Role}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="relative overflow-hidden p-8 rounded-lg">
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-no-repeat"
            style={{
              backgroundImage: 'url(/background/bisubilar.jpg)',
              backgroundPosition: 'center top',
              filter: 'brightness(0.7)'
            }}
          ></div>
          
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/15 to-black/25"></div>
          
          <div className="relative z-10">
            <h2 className="text-center mb-8 text-white drop-shadow-2xl text-5xl font-extrabold animate-fade-in-up" style={{ opacity: 0 }}>Get Involved</h2>
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-white text-xl mb-8 drop-shadow-lg font-medium leading-relaxed animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.2s' }}>
                The Bohol Cacao Industry Information System is a collaborative platform designed to strengthen 
                the entire cacao industry ecosystem. Whether you're a farmer, processor, researcher, 
                government agency, or stakeholder in the cacao value chain, we welcome your participation 
                and contribution to this platform.
              </p>
              <div className="grid md:grid-cols-3 gap-6 text-base mb-8">
                <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg p-6 shadow-xl card-hover animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.3s' }}>
                  <h4 className="text-white mb-3 font-bold drop-shadow-md text-2xl">For Farmers</h4>
                  <p className="text-white text-lg leading-relaxed">Register your farm profile, connect with processors, share best practices, and access market opportunities across Bohol.</p>
                </div>
                <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg p-6 shadow-xl card-hover animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.4s' }}>
                  <h4 className="text-white mb-3 font-bold drop-shadow-md text-2xl">For Processors</h4>
                  <p className="text-white text-lg leading-relaxed">List your processing facility, expand your supplier network, analyze production capacity, and connect with quality cacao sources.</p>
                </div>
                <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-lg p-6 shadow-xl card-hover animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.5s' }}>
                  <h4 className="text-white mb-3 font-bold drop-shadow-md text-2xl">For Partners</h4>
                  <p className="text-white text-lg leading-relaxed">Collaborate with us to enhance the platform, access industry data for research, and support sustainable cacao development.</p>
                </div>
              </div>
              <div className="backdrop-blur-md bg-white/15 border border-white/30 p-8 rounded-lg shadow-2xl animate-scale-in" style={{ opacity: 0, animationDelay: '0.6s' }}>
                <h4 className="text-white mb-6 font-bold drop-shadow-md text-3xl">Current System Coverage</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="stat-card">
                    <div className="text-4xl text-white mb-2 font-extrabold drop-shadow-lg">{statsLoading ? '...' : farmerCount}</div>
                    <div className="text-white text-lg font-semibold">Registered Farmers</div>
                  </div>
                  <div className="stat-card">
                    <div className="text-4xl text-white mb-2 font-extrabold drop-shadow-lg">{statsLoading ? '...' : processorCount}</div>
                    <div className="text-white text-lg font-semibold">Active Processors</div>
                  </div>
                  <div className="stat-card">
                    <div className="text-4xl text-white mb-2 font-extrabold drop-shadow-lg">{statsLoading ? '...' : municipalityCount}</div>
                    <div className="text-white text-lg font-semibold">Municipalities</div>
                  </div>
                  <div className="stat-card">
                    <div className="text-4xl text-white mb-2 font-extrabold drop-shadow-lg">{statsLoading ? '...' : totalHectares.toLocaleString()}</div>
                    <div className="text-white text-lg font-semibold">Hectares</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}